import "./instantQuotation.css";
import { Button } from "primereact/button";

import { Dialog } from "primereact/dialog";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
    closeInstantQuotationModal,
    instantQuotationSelector,
    openSelectVesselModal,
    postInstantQuotationThunk,
    putInstantQuotationThunk,
    setInvalidShip,
    setQuotationRequestState,
    setSelectedInspectionType
} from "../../features/instantQuotationSlice";


import { Trans, useTranslation } from "react-i18next";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { QuoteDatePicker } from "./QuoteDatePicker";
import { LocationDropdown } from "./LocationDropdown";
import { QuotationCreateRequest } from "../../types/quotations/QuotationCreateRequest";
import { QuotationRequestState } from "../../types/quotations/QuotationRequestState";
import { successToast, warningToast } from "@idwal/idwal-react-components";
import { QuotationDialogType } from "../../types/QuotationDialogType";
import { getQuotationExtrasThunk, getQuotationThunk } from "../../features/quotationsSlice";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { QUOTATIONS_ROUTE } from "../../config/router";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { normaliseDate } from "../../utils/dates";
import { Message } from "primereact/message";

export interface LocationData {
    Id: string;
    Name: string;
    Country: string;
    Code: string;
    Region: string;
    SearchField: string;
}

export const InstantQuotation = () => {


    const { user } = useAuthenticator((context) => [
        context.authStatus,
        context.user
    ]);

    const {
        modalOpen,
        inspectionTypeOptions,
        selectedInspectionType,
        selectedInstantQuotationVessel,
        selectedLocation,
        startDate,
        endDate,
        quotationRequestState,
        quotationDialogType,
        quotationId,
        invalidShip
    } = useAppSelector(instantQuotationSelector);
    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const openVesselSelect = () => {
        dispatch(openSelectVesselModal());
    };

    useEffect(() => {
        if (quotationRequestState == QuotationRequestState.CompleteLead || quotationRequestState == QuotationRequestState.CompleteDraft || quotationRequestState == QuotationRequestState.CompleteOffer) {
            dispatch(closeInstantQuotationModal());
            dispatch(setQuotationRequestState(QuotationRequestState.Idle));
            let message = (quotationRequestState === QuotationRequestState.CompleteLead)
                ? t("instantQuotation.instantQuotationSuccessLead")
                : t("instantQuotation.instantQuotationSuccessDraft");
    
            if(quotationDialogType == QuotationDialogType.Edit) {
                message = (quotationRequestState === QuotationRequestState.CompleteOffer)
                    ? t("instantQuotation.QuotationUpdated")
                    : t("instantQuotation.QuotationReview");
            }
            dispatch(getQuotationThunk({ quotationId: quotationId, user: user }));
            dispatch(getQuotationExtrasThunk({ quotationId: quotationId, user: user }));
            dispatch(setInvalidShip(false));
            successToast(message);
            navigate(`/${QUOTATIONS_ROUTE}/` + quotationId);
        }
    
        if (quotationRequestState === QuotationRequestState.Failed) {
            dispatch(closeInstantQuotationModal());
            dispatch(setQuotationRequestState(QuotationRequestState.Idle));
            warningToast(t("instantQuotation.instantQuotationSuccessFailure"));
        }

        if (quotationRequestState === QuotationRequestState.InvalidShip) {
            dispatch(setQuotationRequestState(QuotationRequestState.Idle));
            dispatch(setInvalidShip(true));
        }
    }, [quotationRequestState]);

    const instantQuoteButton = () => {
        const hasValidDataPoints = selectedInstantQuotationVessel && selectedInspectionType && selectedLocation && startDate;

        const buttonLabel = quotationDialogType == QuotationDialogType.New ? t("instantQuotation.createQuotation") : t("instantQuotation.editQuotationButton");

        if (hasValidDataPoints && quotationRequestState == QuotationRequestState.Idle) {
            return <Button
                style={{ width: "160px", height: "52px" }}
                label={buttonLabel}
                data-cy={"open-instant-quotation"}
                className="p-button"
                onClick={() => {

                    let id = null;
                    if (quotationDialogType == QuotationDialogType.Edit) {
                        id = quotationId;
                    }

                    let endDateString = null;

                    if (endDate != null) {
                        endDateString = normaliseDate(endDate);
                    }


                    const request: QuotationCreateRequest = {
                        locationId: selectedLocation["Id"],
                        inspectionTypeId: selectedInspectionType["Id"],
                        shipImo: selectedInstantQuotationVessel["LRIMOShipNo"],
                        startDate: normaliseDate(startDate),
                        endDate: endDateString,
                        id: id
                    };
                    if (quotationDialogType ==  QuotationDialogType.New) {
                        dispatch(postInstantQuotationThunk(request));
                    }

                    if (quotationDialogType == QuotationDialogType.Edit) {
                        dispatch(putInstantQuotationThunk(request));
                    }
                }
                }
            />;
        }


        let icon = "";
        let label = buttonLabel;
        if (quotationRequestState == QuotationRequestState.Processing) {
            icon = "pi pi-spin pi-spinner";
            label = "";
        }

        return <Button
            style={{ width: "160px", height: "52px" }}
            icon={icon}
            disabled={true}
            label={label}
            data-cy={"open-instant-quotation"}
            className="p-button"
        />;

    };

    const header = quotationDialogType == QuotationDialogType.New ? t("instantQuotation.newQuotation") : t("instantQuotation.editQuotation");

    return (
        <Dialog
            blockScroll
            header={header}
            visible={modalOpen}
            style={{ width: "42.5rem" }}
            onHide={() => dispatch(closeInstantQuotationModal())}
            draggable={false}
            data-cy="instant-quotation-dialog"
        >
            {invalidShip && <Message severity="warn" className={"mb-5"} text={<Trans
                                    i18nKey={"instantQuotation.invalidShip"}
                                    components={[
                                        <a content="Mail link for Idwal Enquiries" className="summary-link"
                                           key="contact-us" href="mailto:enquiries@idwalmarine.com" />]
                                    }
                                />} />}
            <div className="field grid mb-3" data-cy="vessel-select-container">
                <label className="col-12 mb-2 md:col-3 md:mb-0" data-cy="vessel-select-label">{t("instantQuotation.vesselSelect")}</label>
                <div className="col-12 md:col-9 flex justify-content-end" data-cy="vessel-select-wrapper">
                    <InputText
                        value={selectedInstantQuotationVessel ? selectedInstantQuotationVessel?.ShipName + " • " + selectedInstantQuotationVessel?.LRIMOShipNo + " • " + selectedInstantQuotationVessel?.ShipTypeName : t("instantQuotation.searchPlaceholder")}
                        className={`dropdownContainer ${invalidShip ? "invalid-ship" : ""}`}
                        data-cy="vessel-select-input"
                        readOnly
                        placeholder={t("instantQuotation.searchPlaceholder")}
                        onClick={openVesselSelect}
                    />
                </div>
                {invalidShip && <span className="invalid-ship-text">{t("instantQuotation.invalidShipSelected")}</span>}
            </div>
            <div className="field grid mb-3" data-cy="inspection-type-container">
                <label className="col-12 mb-2 md:col-3 md:mb-0" data-cy="inspection-type-label">{t("instantQuotation.inspectionType")}</label>
                <div className="col-12 md:col-9 flex justify-content-end" data-cy="inspection-type-wrapper">
                    <Dropdown
                        value={selectedInspectionType}
                        onChange={(e) => {
                            dispatch(setSelectedInspectionType(e.value));
                        }
                        }
                        options={inspectionTypeOptions}
                        optionLabel="Name"
                        placeholder={t("instantQuotation.selectAnInspectionType")}
                        className={"dropdownContainer"}
                        data-cy="inspection-type-dropdown"                        
                    />
                </div>
            </div>
            <LocationDropdown />
            <QuoteDatePicker />

            <div className="flex align-items-center justify-content-center mt-4">
                <Button
                    onClick={() => dispatch(closeInstantQuotationModal())}
                    className="p-button p-component p-button-text"
                >
                    <span
                        data-cy="close-instant-quotation-modal"
                        className="p-button-label p-c w-9rem"
                    >
                        {t("instantQuotation.cancel")}
                    </span>
                </Button>

                {instantQuoteButton()}

            </div>
        </Dialog>
    );
};
